import * as THREE from 'three';

import {
    assembleConstellationDerivations,
    assembleItemDerivations,
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildSphereShape } from '@toolproof-core/visualization/_pure/shapes';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { Constellation } from '@toolproof-core/visualization/_contract/constellation';
import type {
    ConnectorEndpointRef,
} from '@toolproof-core/visualization/_contract/item';
import type { ConstellationKey, ItemKey, SpaceKey } from '@toolproof-core/visualization/_contract/key';
import type {
    InterpolatedFramePatch,
    SpaceDerivationFrame,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { buildBooleanRodDerivations } from '@/adapters/primitive/_pure/derivation/boolean/rod';
import {
    buildNaturalAxialTieConstellationDerivation,
    buildNaturalAxialTieItemCollectionDerivation,
} from '@/adapters/primitive/_pure/derivation/natural/axialTies';
import { buildPrimitiveAxisRodDerivations } from '@/adapters/primitive/_pure/derivation/natural/axisRod';
import { buildNaturalAngularBandLabelDerivations, buildNaturalRadialBandLabelDerivations } from '@/adapters/primitive/_pure/derivation/natural/bandLabels';
import { buildNaturalCanonicalZeroMarkerDerivations } from '@/adapters/primitive/_pure/derivation/natural/canonicalZeroMarkers';
import { buildNaturalRayScanningDerivations } from '@/adapters/primitive/_pure/derivation/natural/rayScanning';
import { buildSettlementWallDerivations } from '@/adapters/primitive/_pure/derivation/natural/settlementWalls';
import {
    buildBooleanDiscConstellationDerivations,
    buildNaturalPowerConstellationDerivations,
    toPrimitiveBandPosition,
    toPrimitiveBandRotation,
    toPrimitiveBandSpin,
} from '@/adapters/primitive/_pure/derivation/constellations';
import { buildPrimitiveHubDerivations } from '@/adapters/primitive/_pure/derivation/hub';
import {
    buildBooleanDiscItemCollectionDerivations,
    buildNaturalDigitItemCollectionDerivations,
} from '@/adapters/primitive/_pure/derivation/itemFactories';
import {
    toPrimitivePowerConstellationKey,
    toPrimitiveHubConstellationKey,
    toPrimitiveWheelConnectorAnchorCollectionKey,
    toPrimitiveWheelConnectorAnchorConstellationKey,
    toPrimitiveWheelConnectorAnchorItemKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import { buildPrimitiveWheelTitleDerivations } from '@/adapters/primitive/_pure/derivation/wheelLabels';
import { isPrimitiveDiscWheel, toPrimitiveHubRingRadius, toWheelLayout, toWheelPosition } from '@/adapters/primitive/_pure/derivation/wheels';
import type { BooleanRodState } from '@/adapters/primitive/_contract/boolean/rod';
import type { Config, PrimitiveWheelRole, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

export type PrimitiveRodScope = string;

export type PrimitiveRodPlacement = {
    axis: readonly [number, number, number];
    /** Rigid graph placement; unlike depth anchors, preserves lateral symmetry and section lengths. */
    origin?: readonly [number, number, number];
    positionByRole: Partial<Record<PrimitiveWheelRole, readonly [number, number, number]>>;
    titlePositionByRole?: Partial<Record<PrimitiveWheelRole, readonly [number, number, number]>>;
    /** Each role's own item-stacking direction; standoffs project along its negation. */
    directionByRole?: Partial<Record<PrimitiveWheelRole, readonly [number, number, number]>>;
};

function normalizeAxis(axis: PrimitiveRodPlacement['axis']): THREE.Vector3 {
    const vector = new THREE.Vector3(...axis);
    return vector.lengthSq() > 1e-12 ? vector.normalize() : new THREE.Vector3(0, 0, 1);
}

export function toPrimitiveRodPlacementBasis(placement: PrimitiveRodPlacement) {
    const axis = normalizeAxis(placement.axis);
    const referenceUp = Math.abs(axis.y) < 0.999
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(0, 0, 1);
    const tangent = new THREE.Vector3().crossVectors(axis, referenceUp).normalize();
    const up = new THREE.Vector3().crossVectors(tangent, axis).normalize();
    return { axis, tangent, up };
}

function toPlacedBandRotation(spin: number, placement: PrimitiveRodPlacement): [number, number, number] {
    const { axis, tangent, up } = toPrimitiveRodPlacementBasis(placement);
    const basis = new THREE.Matrix4().makeBasis(tangent, axis, up);
    const quaternion = new THREE.Quaternion().setFromRotationMatrix(basis)
        .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), spin + Math.PI));
    const euler = new THREE.Euler().setFromQuaternion(quaternion, 'XYZ');
    return [euler.x, euler.y, euler.z];
}

function toPlacedWheelRotation(rotation: [number, number, number], placement: PrimitiveRodPlacement): [number, number, number] {
    const { axis, tangent, up } = toPrimitiveRodPlacementBasis(placement);
    // Position placement reflects primitive X. Reflect local Y too to preserve a proper rotation
    // while mapping every point of the local XZ sector plane through the same placement basis.
    const matrix = new THREE.Matrix4().makeBasis(tangent, up, axis)
        .multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(...rotation, 'XYZ')))
        .multiply(new THREE.Matrix4().makeScale(1, -1, 1));
    const euler = new THREE.Euler().setFromRotationMatrix(matrix, 'XYZ');
    return [euler.x, euler.y, euler.z];
}

function mapPrimitiveDepth(args: {
    depth: number;
    config: Config;
    placement: PrimitiveRodPlacement;
}): THREE.Vector3 {
    const layout = toWheelLayout({
        config: args.config,
        operandSchedulingMode: args.config.natural.operandSchedulingMode,
    });
    const anchors = Object.entries(args.placement.positionByRole)
        .flatMap(([role, position]) => position === undefined ? [] : [{
            depth: toWheelPosition({ layout, role: role as PrimitiveWheelRole })[2],
            position: new THREE.Vector3(...position),
        }])
        .sort((left, right) => left.depth - right.depth);
    if (anchors.length === 0) return new THREE.Vector3(0, 0, args.depth);
    if (anchors.length === 1) {
        return anchors[0].position.clone().addScaledVector(
            normalizeAxis(args.placement.axis),
            args.depth - anchors[0].depth,
        );
    }

    let lower = anchors[0];
    let upper = anchors[1];
    for (let index = 1; index < anchors.length; index += 1) {
        const candidate = anchors[index];
        if (args.depth <= candidate.depth) {
            upper = candidate;
            lower = anchors[index - 1];
            break;
        }
        lower = anchors[index - 1];
        upper = candidate;
    }
    const depthSpan = upper.depth - lower.depth;
    const progress = Math.abs(depthSpan) <= 1e-12 ? 0 : (args.depth - lower.depth) / depthSpan;
    return lower.position.clone().lerp(upper.position, progress);
}

function placePrimitivePosition(args: {
    position: readonly [number, number, number];
    config: Config;
    placement: PrimitiveRodPlacement;
    role?: PrimitiveWheelRole;
}): [number, number, number] {
    if (args.role !== undefined
        && (args.placement.origin === undefined || args.role === 'boolean-output')
        && args.position[0] === 0 && args.position[1] === 0) {
        const declaredPosition = toWheelPosition({
            layout: toWheelLayout({ config: args.config, operandSchedulingMode: args.config.natural.operandSchedulingMode }),
            role: args.role,
        });
        const anchor = args.placement.positionByRole[args.role];
        if (anchor !== undefined && args.position[2] === declaredPosition[2]) return [...anchor];
    }
    const { axis, tangent, up } = toPrimitiveRodPlacementBasis(args.placement);
    const mapped = args.placement.origin === undefined ? mapPrimitiveDepth({
        depth: args.position[2],
        config: args.config,
        placement: args.placement,
    }) : new THREE.Vector3(...args.placement.origin).addScaledVector(axis, args.position[2]);
    // Placed comparison cells use negative tangent; the travelling eye must share that handedness.
    mapped.addScaledVector(tangent, args.role === 'less-than-decision'
        && args.config.natural.display.comparison.naturalLessThanPresentationMode === 'travelling-eye'
        ? -args.position[0] : args.position[0]);
    mapped.addScaledVector(up, args.position[1]);
    return [mapped.x, mapped.y, mapped.z];
}

function placePrimitiveWheels(args: {
    wheels: readonly PrimitiveWheelState[];
    config: Config;
    placement: PrimitiveRodPlacement | undefined;
}): readonly PrimitiveWheelState[] {
    if (args.placement === undefined) return args.wheels;
    const placement = args.placement;
    const axis = normalizeAxis(placement.axis);
    // Offsets are primitive depth deltas; the rod may space depth unevenly, so each one is re-measured along the axis.
    const placeOffset = (base: readonly [number, number, number], offset: number): number => {
        const from = new THREE.Vector3(...placePrimitivePosition({ position: base, config: args.config, placement }));
        const to = new THREE.Vector3(...placePrimitivePosition({
            position: [base[0], base[1], base[2] + offset],
            config: args.config,
            placement,
        }));
        return to.sub(from).dot(axis);
    };
    return args.wheels.map((wheel) => ({
        ...wheel,
        ...(wheel.role === 'less-than-decision'
            && args.config.natural.display.comparison.naturalLessThanPresentationMode === 'travelling-eye' ? {
            titlePosition: placement.titlePositionByRole?.[wheel.role] ?? placePrimitivePosition({
                position: toWheelPosition({
                    layout: toWheelLayout({ config: args.config, operandSchedulingMode: args.config.natural.operandSchedulingMode }),
                    role: wheel.role,
                }),
                config: args.config,
                placement,
            }),
        } : {}),
        ...(wheel.cellOverridesByPower === undefined ? {} : {
            cellOverridesByPower: Object.fromEntries(Object.entries(wheel.cellOverridesByPower).map(([power, byDigit]) => [
                power,
                Object.fromEntries(Object.entries(byDigit ?? {}).map(([digit, override]) => [
                    digit,
                    override?.offset === undefined
                        ? override
                        : { ...override, offset: placeOffset(toPrimitiveBandPosition(wheel, Number(power)), override.offset) },
                ])),
            ])),
        }),
        position: placePrimitivePosition({ position: wheel.position, config: args.config, placement, role: wheel.role }),
        ...(wheel.comparisonOrbitCenter === undefined ? {} : {
            comparisonOrbitCenter: placePrimitivePosition({ position: wheel.comparisonOrbitCenter, config: args.config, placement }),
        }),
        ...(wheel.positionByPower === undefined ? {} : {
            positionByPower: Object.fromEntries(Object.entries(wheel.positionByPower).flatMap(([power, position]) => (
                position === undefined
                    ? []
                    : [[power, placePrimitivePosition({ position, config: args.config, placement, role: wheel.role })]]
            ))),
        }),
    }));
}

function orientPrimitiveRodFrame(args: {
    frame: SpaceDerivationFrame;
    placement: PrimitiveRodPlacement | undefined;
    wheels: readonly PrimitiveWheelState[];
}): SpaceDerivationFrame {
    if (args.placement === undefined) return args.frame;
    const entityConstellationByKey = args.frame.constellationParametricState?.entityConstellationByKey;
    if (entityConstellationByKey === undefined) return args.frame;

    return {
        ...args.frame,
        constellationParametricState: {
            ...args.frame.constellationParametricState,
            entityConstellationByKey: Object.fromEntries(args.frame.constellations.flatMap((constellation) => {
                if (constellation.constellationKind !== 'entity') return [];
                const state = entityConstellationByKey[constellation.constellationKey];
                if (state?.layout?.layoutKind !== 'sector-ring') return [[constellation.constellationKey, state]];
                const spin = state.layout.rotation?.[1] ?? 0;
                const wheel = args.wheels.find((wheel) => wheel.wheelRotation !== undefined && (
                    constellation.constellationKey === toPrimitiveHubConstellationKey({ wheelRole: wheel.role })
                    || wheel.powers.some((power) => constellation.constellationKey === toPrimitivePowerConstellationKey({
                        wheelRole: wheel.role, variant: wheel.variant, power,
                    }))
                ));
                return [[constellation.constellationKey, {
                    ...state,
                    layout: {
                        ...state.layout,
                        rotation: wheel?.wheelRotation
                            ? toPlacedWheelRotation(wheel.wheelRotation, args.placement!)
                            : toPlacedBandRotation(spin, args.placement!),
                    },
                }]];
            })),
        },
    };
}

export function toScopedPrimitiveRodKey<TKey extends string>(key: TKey, scope: PrimitiveRodScope | undefined): TKey {
    if (scope === undefined) return key;
    return key.replace(':primitive:', `:primitive:${encodeURIComponent(scope)}:`) as TKey;
}

function scopeConnectorEndpoint(endpoint: ConnectorEndpointRef, scope: PrimitiveRodScope): ConnectorEndpointRef {
    if (endpoint.endpointKind === 'static') {
        return { ...endpoint, itemKey: toScopedPrimitiveRodKey(endpoint.itemKey, scope) };
    }
    return {
        ...endpoint,
        ringItemKey: toScopedPrimitiveRodKey(endpoint.ringItemKey, scope),
        anglePolicy: endpoint.anglePolicy.anglePolicyKind === 'match-item-in-ring'
            ? {
                ...endpoint.anglePolicy,
                matchItemKey: toScopedPrimitiveRodKey(endpoint.anglePolicy.matchItemKey, scope),
            }
            : endpoint.anglePolicy,
    };
}

function scopeItemCollection(collection: ItemCollection, scope: PrimitiveRodScope): ItemCollection {
    if (collection.itemCollectionKind === 'entity') {
        return {
            ...collection,
            itemCollectionKey: toScopedPrimitiveRodKey(collection.itemCollectionKey, scope),
            items: collection.items.map((item) => ({
                ...item,
                itemKey: toScopedPrimitiveRodKey(item.itemKey, scope),
            })),
        };
    }
    return {
        ...collection,
        itemCollectionKey: toScopedPrimitiveRodKey(collection.itemCollectionKey, scope),
        items: collection.items.map((item) => ({
            ...item,
            itemKey: toScopedPrimitiveRodKey(item.itemKey, scope),
            endpoints: {
                from: scopeConnectorEndpoint(item.endpoints.from, scope),
                to: scopeConnectorEndpoint(item.endpoints.to, scope),
            },
        })),
    };
}

function scopeConstellation(constellation: Constellation, scope: PrimitiveRodScope): Constellation {
    if (constellation.constellationKind === 'entity') {
        return {
            ...constellation,
            constellationKey: toScopedPrimitiveRodKey(constellation.constellationKey, scope),
            itemCollectionKey: toScopedPrimitiveRodKey(constellation.itemCollectionKey, scope),
            anchor: {
                ...constellation.anchor,
                itemKey: toScopedPrimitiveRodKey(constellation.anchor.itemKey, scope),
            },
        };
    }
    return {
        ...constellation,
        constellationKey: toScopedPrimitiveRodKey(constellation.constellationKey, scope),
        itemCollectionKey: toScopedPrimitiveRodKey(constellation.itemCollectionKey, scope),
    };
}

function scopeRecordKeys<T>(record: Partial<Record<string, T>> | undefined, scope: PrimitiveRodScope) {
    if (record === undefined) return undefined;
    return Object.fromEntries(
        Object.entries(record).map(([key, value]) => [toScopedPrimitiveRodKey(key, scope), value]),
    ) as Partial<Record<string, T>>;
}

function scopePrimitiveRodFrame(frame: SpaceDerivationFrame, scope: PrimitiveRodScope | undefined): SpaceDerivationFrame {
    if (scope === undefined) return frame;
    return {
        ...frame,
        itemCollections: frame.itemCollections.map((collection) => scopeItemCollection(collection, scope)),
        constellations: frame.constellations.map((constellation) => scopeConstellation(constellation, scope)),
        itemParametricState: frame.itemParametricState === undefined
            ? undefined
            : {
                entityItemByKey: scopeRecordKeys(frame.itemParametricState.entityItemByKey, scope),
                connectorItemByKey: scopeRecordKeys(frame.itemParametricState.connectorItemByKey, scope),
            },
        constellationParametricState: frame.constellationParametricState === undefined
            ? undefined
            : {
                entityConstellationByKey: scopeRecordKeys(
                    frame.constellationParametricState.entityConstellationByKey,
                    scope,
                ),
                connectorConstellationByKey: scopeRecordKeys(
                    frame.constellationParametricState.connectorConstellationByKey,
                    scope,
                ),
            },
    };
}

function buildPrimitiveWheelConnectorAnchorDerivations(args: {
    config: Config;
    wheels: readonly PrimitiveWheelState[];
    placement?: PrimitiveRodPlacement;
}) {
    const horizontal = args.placement === undefined
        ? new THREE.Vector3(1, 0, 0)
        : toPrimitiveRodPlacementBasis(args.placement).tangent;
    const itemCollectionDerivations = [];
    const constellationDerivations = [];

    for (const wheel of args.wheels) {
        if (wheel.variant !== undefined) continue;
        const outermostPower = Math.max(0, ...wheel.powers);
        const outerRadius = wheel.valueKind === 'placeholder' || wheel.valueKind === 'comparison-helper'
            ? toPrimitiveHubRingRadius(args.config)
            : args.config.base.display.wheel.innerRadius
                + outermostPower * (args.config.base.display.wheel.ringWidth + args.config.natural.display.wheel.ringGap)
                + args.config.base.display.wheel.ringWidth;

        for (const side of ['min', 'max'] as const) {
            const ref = { wheelRole: wheel.role, side };
            const itemKey = toPrimitiveWheelConnectorAnchorItemKey(ref);
            const direction = side === 'min' ? -1 : 1;
            const position: [number, number, number] = [
                wheel.position[0] + horizontal.x * outerRadius * direction,
                wheel.position[1] + horizontal.y * outerRadius * direction,
                wheel.position[2] + horizontal.z * outerRadius * direction,
            ];
            itemCollectionDerivations.push(toEntityItemCollectionDerivation({
                itemCollectionKey: toPrimitiveWheelConnectorAnchorCollectionKey(ref),
                itemDerivations: [defineEntityItemDerivation({
                    item: {
                        itemKind: 'entity',
                        itemKey,
                        name: `${wheel.role} Workflow connector ${side} anchor`,
                        description: 'Invisible endpoint at the current outer rim of the wheel.',
                    },
                    parametricState: {
                        shape: buildSphereShape({ radius: 0.01 }),
                        visualStyle: { opacity: 0 },
                    },
                })],
            }));
            constellationDerivations.push(defineEntityConstellationDerivation({
                constellation: {
                    constellationKind: 'entity',
                    constellationKey: toPrimitiveWheelConnectorAnchorConstellationKey(ref),
                    itemCollectionKey: toPrimitiveWheelConnectorAnchorCollectionKey(ref),
                    anchor: { itemKey, anchorKey: 'center' },
                    meta: { wheelRole: wheel.role, connectorSide: side },
                },
                parametricState: {
                    layout: {
                        layoutKind: 'sector-ring',
                        anchor: { anchorKind: 'position', position },
                        innerRadius: 0,
                        outerRadius: 0.01,
                        angularGapFraction: 0,
                        arcSegments: 3,
                    },
                },
            }));
        }
    }

    return { itemCollectionDerivations, constellationDerivations };
}

export function buildPrimitiveRodFrame(args: {
    spaceKey: SpaceKey;
    config: Config;
    wheels: readonly PrimitiveWheelState[];
    booleanRod?: BooleanRodState;
    scope?: PrimitiveRodScope;
    placement?: PrimitiveRodPlacement;
    /** Applied per-wheel after placement mapping, so it can operate on the final world-space position. */
    postPlacementOverride?: (wheel: PrimitiveWheelState) => PrimitiveWheelState;
}): SpaceDerivationFrame {
    const { spaceKey, config } = args;
    const placedWheels = placePrimitiveWheels({
        wheels: args.wheels,
        config,
        placement: args.placement,
    });
    const wheels = args.postPlacementOverride ? placedWheels.map(args.postPlacementOverride) : placedWheels;
    const naturalWheels = wheels.filter((wheel) => !isPrimitiveDiscWheel(wheel));
    const placementBasis = args.placement === undefined ? undefined : toPrimitiveRodPlacementBasis(args.placement);
    const axialTieItemCollectionDerivation = buildNaturalAxialTieItemCollectionDerivation(config, naturalWheels);
    const wheelTitles = buildPrimitiveWheelTitleDerivations(
        config,
        wheels,
        placementBasis === undefined
            ? undefined
            : [placementBasis.up.x, placementBasis.up.y, placementBasis.up.z],
    );
    const radialBandLabels = buildNaturalRadialBandLabelDerivations(config, naturalWheels);
    const angularBandLabels = buildNaturalAngularBandLabelDerivations(config, naturalWheels);
    const axisRod = args.booleanRod === undefined || wheels.some(wheel => wheel.role === 'multiplier')
        || args.booleanRod.preserveNaturalAxis
        ? buildPrimitiveAxisRodDerivations(config, wheels.filter(wheel => wheel.role !== 'less-than-decision')) : null;
    const booleanRod = args.booleanRod === undefined ? null : buildBooleanRodDerivations(
        args.placement === undefined ? args.booleanRod : {
            segments: args.booleanRod.segments.map((segment) => {
                const { tangent, up, axis } = toPrimitiveRodPlacementBasis(args.placement!);
                const lateral = segment.profile === undefined ? undefined : new THREE.Vector3()
                    .addScaledVector(tangent, segment.profile.lateral[0])
                    .addScaledVector(up, segment.profile.lateral[1])
                    .addScaledVector(axis, segment.profile.lateral[2]);
                return {
                    ...segment,
                    from: placePrimitivePosition({ position: segment.from, config, placement: args.placement! }),
                    to: placePrimitivePosition({ position: segment.to, config, placement: args.placement! }),
                    ...(segment.profile === undefined || lateral === undefined ? {} : {
                        profile: { ...segment.profile, lateral: [lateral.x, lateral.y, lateral.z] as [number, number, number] },
                    }),
                };
            }),
        },
    );
    const hubs = buildPrimitiveHubDerivations(config, wheels);
    const canonicalZeroMarkers = buildNaturalCanonicalZeroMarkerDerivations(
        config,
        naturalWheels,
        placementBasis === undefined
            ? undefined
            : {
                xAxis: [-placementBasis.tangent.x, -placementBasis.tangent.y, -placementBasis.tangent.z],
                yAxis: [placementBasis.up.x, placementBasis.up.y, placementBasis.up.z],
            },
    );
    const comparisonRays = buildNaturalRayScanningDerivations(
        config,
        wheels,
        placementBasis === undefined
            ? undefined
            : {
                xAxis: [-placementBasis.tangent.x, -placementBasis.tangent.y, -placementBasis.tangent.z],
                yAxis: [placementBasis.up.x, placementBasis.up.y, placementBasis.up.z],
            },
    );
    const workflowConnectorAnchors = buildPrimitiveWheelConnectorAnchorDerivations({
        config,
        wheels,
        placement: args.placement,
    });
    const settlementWalls = buildSettlementWallDerivations({
        config,
        wheels,
        axis: placementBasis === undefined
            ? [0, 0, 1]
            : [placementBasis.axis.x, placementBasis.axis.y, placementBasis.axis.z],
    });
    const itemCollectionDerivations = [
        ...wheels.filter((wheel) => wheel.valueKind !== 'placeholder' || wheel.role === 'less-than-decision').flatMap((wheel) => isPrimitiveDiscWheel(wheel)
            ? buildBooleanDiscItemCollectionDerivations(config, wheel)
            : buildNaturalDigitItemCollectionDerivations(config, wheel)),
        ...hubs.itemCollectionDerivations,
        ...wheelTitles.itemCollectionDerivations,
        ...radialBandLabels.itemCollectionDerivations,
        ...radialBandLabels.connectorItemCollectionDerivations,
        ...angularBandLabels.itemCollectionDerivations,
        ...workflowConnectorAnchors.itemCollectionDerivations,
        ...(axisRod?.entityItemCollectionDerivations ?? []),
        ...(booleanRod?.entityItemCollectionDerivations ?? []),
        ...(canonicalZeroMarkers?.entityItemCollectionDerivations ?? []),
        ...(comparisonRays?.entityItemCollectionDerivations ?? []),
        ...settlementWalls.entityItemCollectionDerivations,
        ...(axialTieItemCollectionDerivation === null ? [] : [axialTieItemCollectionDerivation]),
        ...(axisRod ? [axisRod.connectorItemCollectionDerivation] : []),
        ...(booleanRod ? [booleanRod.connectorItemCollectionDerivation] : []),
        ...(comparisonRays ? [comparisonRays.connectorItemCollectionDerivation] : []),
    ];
    const constellationDerivations = [
        ...wheels.filter((wheel) => wheel.valueKind !== 'placeholder' || wheel.role === 'less-than-decision').flatMap((wheel) => isPrimitiveDiscWheel(wheel)
            ? buildBooleanDiscConstellationDerivations(config, wheel)
            : buildNaturalPowerConstellationDerivations(config, wheel)),
        ...hubs.constellationDerivations,
        ...wheelTitles.constellationDerivations,
        ...radialBandLabels.constellationDerivations,
        ...radialBandLabels.connectorConstellationDerivations,
        ...angularBandLabels.constellationDerivations,
        ...workflowConnectorAnchors.constellationDerivations,
        ...(axisRod?.entityConstellationDerivations ?? []),
        ...(booleanRod?.entityConstellationDerivations ?? []),
        ...(canonicalZeroMarkers?.entityConstellationDerivations ?? []),
        ...(comparisonRays?.entityConstellationDerivations ?? []),
        ...settlementWalls.entityConstellationDerivations,
        ...(axialTieItemCollectionDerivation === null ? [] : [buildNaturalAxialTieConstellationDerivation()]),
        ...(axisRod ? [axisRod.connectorConstellationDerivation] : []),
        ...(booleanRod ? [booleanRod.connectorConstellationDerivation] : []),
        ...(comparisonRays ? [comparisonRays.connectorConstellationDerivation] : []),
    ];
    const itemAssembly = assembleItemDerivations(itemCollectionDerivations);
    const constellationAssembly = assembleConstellationDerivations(constellationDerivations);

    return scopePrimitiveRodFrame(
        orientPrimitiveRodFrame({
            placement: args.placement,
            wheels: args.wheels,
            frame: {
                spaceKey,
                itemCollections: itemAssembly.itemCollections,
                constellations: constellationAssembly.constellations,
                spaceRenderOptions: {
                    backgroundColor: config.base.display.label.backgroundColor,
                    camera: { pose: config.base.camera.pose },
                },
                itemParametricState: itemAssembly.itemParametricState,
                constellationParametricState: constellationAssembly.constellationParametricState,
            },
        }),
        args.scope,
    );
}

type BandLayoutPatch = {
    layout: {
        rotation?: readonly number[];
        anchor?: { position: readonly number[] };
        innerRadius?: number;
        outerRadius?: number;
    };
};

export function buildPrimitiveRodFlightPatch(args: {
    spaceKey: SpaceKey;
    config: Config;
    previousWheels: readonly PrimitiveWheelState[];
    nextWheels: readonly PrimitiveWheelState[];
    previousBooleanRod?: BooleanRodState;
    nextBooleanRod?: BooleanRodState;
    /** Frames derived with the same placement/config, reused by sequential transition construction. */
    previousFrame?: SpaceDerivationFrame;
    nextFrame?: SpaceDerivationFrame;
    scope?: PrimitiveRodScope;
    placement?: PrimitiveRodPlacement;
    postPlacementOverride?: (wheel: PrimitiveWheelState) => PrimitiveWheelState;
}): InterpolatedFramePatch | null {
    const mapWheelPosition = (wheel: PrimitiveWheelState, power: number): readonly number[] => {
        const mappedPosition = args.placement === undefined
            ? toPrimitiveBandPosition(wheel, power)
            : placePrimitivePosition({
                position: toPrimitiveBandPosition(wheel, power),
                config: args.config,
                placement: args.placement,
                role: wheel.role,
            });
        return args.postPlacementOverride
            ? args.postPlacementOverride({ ...wheel, position: mappedPosition as [number, number, number] }).position
            : mappedPosition;
    };
    const previousByKey = new Map<string, { spin: number; position: readonly number[] }>();
    for (const wheel of args.previousWheels) {
        if (wheel.valueKind === 'placeholder' && wheel.role !== 'less-than-decision') continue;
        for (const power of wheel.powers) {
            previousByKey.set(toScopedPrimitiveRodKey(
                toPrimitivePowerConstellationKey({ wheelRole: wheel.role, variant: wheel.variant, power }),
                args.scope,
            ), {
                spin: toPrimitiveBandSpin(wheel, power),
                position: mapWheelPosition(wheel, power),
            });
        }
    }

    const entityConstellationByKey: Record<string, BandLayoutPatch> = {};
    const entityConstellationLocalRotationDeltaByKey: NonNullable<
        InterpolatedFramePatch['entityConstellationLocalRotationDeltaByKey']
    > = {};
    for (const wheel of args.nextWheels) {
        if (wheel.valueKind === 'placeholder' && wheel.role !== 'less-than-decision') continue;
        for (const power of wheel.powers) {
            const constellationKey = toScopedPrimitiveRodKey(
                toPrimitivePowerConstellationKey({ wheelRole: wheel.role, variant: wheel.variant, power }),
                args.scope,
            );
            const previous = previousByKey.get(constellationKey);
            if (previous === undefined) continue;

            const spin = toPrimitiveBandSpin(wheel, power);
            const position = mapWheelPosition(wheel, power);
            const layout: BandLayoutPatch['layout'] = {};

            if (spin !== previous.spin) {
                layout.rotation = args.placement === undefined
                    ? toPrimitiveBandRotation(spin)
                    : toPlacedBandRotation(spin, args.placement);
                if (args.placement !== undefined) {
                    entityConstellationLocalRotationDeltaByKey[constellationKey as ConstellationKey<'entity'>] = {
                        axis: [0, 1, 0],
                        angle: spin - previous.spin,
                    };
                }
            }
            if (position.some((value, index) => value !== previous.position[index])) {
                layout.anchor = { position };
            }
            if (layout.rotation !== undefined || layout.anchor !== undefined) {
                entityConstellationByKey[constellationKey] = { layout };
            }
        }
    }

    const previousFrame = args.previousFrame ?? buildPrimitiveRodFrame({
        spaceKey: args.spaceKey,
        config: args.config,
        wheels: args.previousWheels,
        ...(args.previousBooleanRod === undefined ? {} : { booleanRod: args.previousBooleanRod }),
        scope: args.scope,
        placement: args.placement,
        postPlacementOverride: args.postPlacementOverride,
    });
    const nextFrame = args.nextFrame ?? buildPrimitiveRodFrame({
        spaceKey: args.spaceKey,
        config: args.config,
        wheels: args.nextWheels,
        ...(args.nextBooleanRod === undefined ? {} : { booleanRod: args.nextBooleanRod }),
        scope: args.scope,
        placement: args.placement,
        postPlacementOverride: args.postPlacementOverride,
    });
    for (const [constellationKey, nextState] of Object.entries(
        nextFrame.constellationParametricState?.entityConstellationByKey ?? {},
    )) {
        const previousState = previousFrame.constellationParametricState
            ?.entityConstellationByKey?.[constellationKey as ConstellationKey<'entity'>];
        // Point-anchored rings host single items such as settlement walls, which move with their participants.
        if (nextState?.layout?.layoutKind === 'ring'
            && previousState?.layout?.layoutKind === 'ring'
            && nextState.layout.anchor.anchorKind === 'position'
            && previousState.layout.anchor.anchorKind === 'position') {
            const nextPosition = nextState.layout.anchor.position;
            const previousPosition = previousState.layout.anchor.position;
            if (nextPosition.some((value, index) => value !== previousPosition[index])) {
                entityConstellationByKey[constellationKey] = { layout: { anchor: { position: nextPosition } } };
            }
            continue;
        }
        if (nextState === undefined
            || nextState.layout?.layoutKind !== 'sector-ring'
            || previousState?.layout?.layoutKind !== 'sector-ring'
            || nextState.layout.anchor.anchorKind !== 'position'
            || previousState.layout.anchor.anchorKind !== 'position') continue;
        const nextPosition = nextState.layout.anchor.position;
        const previousPosition = previousState.layout.anchor.position;
        const layout: BandLayoutPatch['layout'] = {
            ...entityConstellationByKey[constellationKey]?.layout,
        };
        if (nextPosition.some((value, index) => value !== previousPosition[index])) {
            layout.anchor = { position: nextPosition };
        }
        if (nextState.layout.innerRadius !== undefined
            && previousState.layout.innerRadius !== undefined
            && nextState.layout.innerRadius !== previousState.layout.innerRadius) {
            layout.innerRadius = nextState.layout.innerRadius;
        }
        if (nextState.layout.outerRadius !== previousState.layout.outerRadius) {
            layout.outerRadius = nextState.layout.outerRadius;
        }
        if (Object.keys(layout).length > 0) entityConstellationByKey[constellationKey] = { layout };
    }
    for (const wheel of args.nextWheels) {
        if (!wheel.wheelRotation) continue;
        const previousWheel = args.previousWheels.find((candidate) => candidate.role === wheel.role
            && candidate.variant === wheel.variant);
        if (!previousWheel?.wheelRotation) continue;
        const rotation = wheel.wheelRotation;
        const previousRotation = previousWheel.wheelRotation;
        for (const key of [
            toPrimitiveHubConstellationKey({ wheelRole: wheel.role }),
            ...wheel.powers.map((power) => toPrimitivePowerConstellationKey({ wheelRole: wheel.role, variant: wheel.variant, power })),
        ]) {
            const placedKey = toScopedPrimitiveRodKey(key, args.scope);
            if (rotation.some((value, index) => value !== previousRotation[index])) {
                const nextLayout = nextFrame.constellationParametricState?.entityConstellationByKey?.[placedKey]?.layout;
                if (nextLayout?.layoutKind !== 'sector-ring') continue;
                entityConstellationByKey[placedKey] = {
                    layout: { ...entityConstellationByKey[placedKey]?.layout, rotation: nextLayout.rotation },
                };
                if (rotation[0] === previousRotation[0] && rotation[2] === previousRotation[2]) {
                    entityConstellationLocalRotationDeltaByKey[placedKey] = {
                        axis: [0, 1, 0],
                        angle: rotation[1] - previousRotation[1],
                    };
                } else {
                    const previousLayout = previousFrame.constellationParametricState?.entityConstellationByKey?.[placedKey]?.layout;
                    if (previousLayout?.layoutKind !== 'sector-ring') continue;
                    const start = new THREE.Quaternion().setFromEuler(new THREE.Euler(...previousLayout.rotation ?? [0, 0, 0]));
                    const finish = new THREE.Quaternion().setFromEuler(new THREE.Euler(...nextLayout.rotation ?? [0, 0, 0]));
                    const delta = start.invert().multiply(finish);
                    if (delta.w < 0) delta.set(-delta.x, -delta.y, -delta.z, -delta.w);
                    const angle = 2 * Math.acos(THREE.MathUtils.clamp(delta.w, -1, 1));
                    if (angle > 1e-8) entityConstellationLocalRotationDeltaByKey[placedKey] = {
                        axis: new THREE.Vector3(delta.x, delta.y, delta.z).normalize().toArray(), angle,
                    };
                }
            }
        }
    }
    type EntityItemPatch = {
        visualStyle?: { opacity: number };
        layoutNormalOffset?: number;
        shape?: {
            height?: number; from?: readonly number[]; to?: readonly number[]; lateral?: readonly number[];
            innerWidth?: number; lipThickness?: number; lipHeight?: number; floorDepth?: number;
            border?: { color?: string; opacity?: number; width?: number };
        };
    };
    const entityItemByKey: Record<string, EntityItemPatch> = {};
    const entityItemRigidProfileRotationByKey: NonNullable<InterpolatedFramePatch['entityItemRigidProfileRotationByKey']> = {};
    const connectorItemByKey: Record<string, { visualStyle: { opacity: number } }> = {};

    for (const [itemKey, nextState] of Object.entries(nextFrame.itemParametricState?.entityItemByKey ?? {})) {
        const previousState = previousFrame.itemParametricState?.entityItemByKey?.[itemKey as ItemKey<'entity'>];
        const previousOpacity = previousState?.visualStyle?.opacity;
        const nextOpacity = nextState?.visualStyle?.opacity;
        const previousOffset = previousState?.layoutNormalOffset;
        const nextOffset = nextState?.layoutNormalOffset;
        const patch: EntityItemPatch = {};
        if (previousOpacity !== undefined && nextOpacity !== undefined && previousOpacity !== nextOpacity) {
            patch.visualStyle = { opacity: nextOpacity };
        }
        if (previousOffset !== undefined && nextOffset !== undefined && previousOffset !== nextOffset) {
            patch.layoutNormalOffset = nextOffset;
        }
        if (previousState?.shape?.shapeKind === 'cylinder-wall'
            && nextState?.shape?.shapeKind === 'cylinder-wall'
            && previousState.shape.height !== nextState.shape.height) {
            patch.shape = { height: nextState.shape.height };
        }
        const previousProfile = previousState?.shape;
        if (previousProfile?.shapeKind === 'extruded-profile'
            && nextState?.shape?.shapeKind === 'extruded-profile') {
            if (args.nextWheels.some((wheel) => wheel.wheelRotation)
                && itemKey.includes('traveller:')) {
                const from = new THREE.Vector3(...previousProfile.to).sub(new THREE.Vector3(...previousProfile.from)).normalize();
                const to = new THREE.Vector3(...nextState.shape.to).sub(new THREE.Vector3(...nextState.shape.from)).normalize();
                const turn = new THREE.Quaternion().setFromUnitVectors(from, to);
                const angle = 2 * Math.acos(THREE.MathUtils.clamp(turn.w, -1, 1));
                if (angle > 1e-8) entityItemRigidProfileRotationByKey[itemKey as ItemKey<'entity'>] = {
                    axis: new THREE.Vector3(turn.x, turn.y, turn.z).normalize().toArray(), angle,
                };
            }
            const shapePatch: NonNullable<EntityItemPatch['shape']> = {};
            if (nextState.shape.border && previousProfile.border) {
                const borderPatch: NonNullable<NonNullable<EntityItemPatch['shape']>['border']> = {};
                for (const field of ['opacity', 'width'] as const) {
                    if (nextState.shape.border[field] !== previousProfile.border[field]) {
                        borderPatch[field] = nextState.shape.border[field];
                    }
                }
                if (Object.keys(borderPatch).length > 0) shapePatch.border = borderPatch;
            }
            for (const field of ['from', 'to', 'lateral'] as const) {
            if (nextState.shape[field].some((value, index) => value !== previousProfile[field][index])) {
                    shapePatch[field] = nextState.shape[field];
                }
                for (const field of ['innerWidth', 'lipThickness', 'lipHeight', 'floorDepth'] as const) {
                    if (nextState.shape[field] !== previousProfile[field]) shapePatch[field] = nextState.shape[field];
                }
            }
            if (Object.keys(shapePatch).length > 0) patch.shape = shapePatch;
        }
        if (Object.keys(patch).length > 0) entityItemByKey[itemKey] = patch;
    }

    for (const [itemKey, nextState] of Object.entries(nextFrame.itemParametricState?.connectorItemByKey ?? {})) {
        const previousState = previousFrame.itemParametricState?.connectorItemByKey?.[itemKey as ItemKey<'connector'>];
        const previousOpacity = previousState?.visualStyle?.opacity;
        const nextOpacity = nextState?.visualStyle?.opacity;
        if (previousOpacity !== undefined && nextOpacity !== undefined && previousOpacity !== nextOpacity) {
            connectorItemByKey[itemKey] = { visualStyle: { opacity: nextOpacity } };
        }
    }

    const hasConstellations = Object.keys(entityConstellationByKey).length > 0;
    const hasLocalRotations = Object.keys(entityConstellationLocalRotationDeltaByKey).length > 0;
    const hasEntityItems = Object.keys(entityItemByKey).length > 0;
    const hasConnectorItems = Object.keys(connectorItemByKey).length > 0;
    const hasItems = hasEntityItems || hasConnectorItems;
    if (!hasConstellations && !hasItems) return null;

    return {
        ...(Object.keys(entityItemRigidProfileRotationByKey).length ? { entityItemRigidProfileRotationByKey } : {}),
        ...(hasConstellations ? { constellationParametricState: { entityConstellationByKey } } : {}),
        ...(hasLocalRotations ? { entityConstellationLocalRotationDeltaByKey } : {}),
        ...(hasItems ? {
            itemParametricState: {
                ...(hasEntityItems ? { entityItemByKey } : {}),
                ...(hasConnectorItems ? { connectorItemByKey } : {}),
            },
        } : {}),
    } as InterpolatedFramePatch;
}